import { z } from 'zod';
import { CreateLocation } from './tenancy.js';
import { Email, IsoDateTime, NonEmptyName, Uuid } from '../primitives.js';

/**
 * WS6 — salon administration & onboarding contract.
 *
 * Staff invites carry a token whose raw value is sent only in the invite email; the API stores a
 * sha256 hash + a short non-secret prefix (same idiom as personal access tokens) and NEVER returns
 * the raw token. Password resets follow the same hash-and-prefix pattern but on the global pool.
 */

/**
 * Minimum password length, kept identical to RegisterRequest in entities/auth.ts so every place a
 * Salonomy password is set enforces the same rule. (Update both together if the policy changes.)
 */
const Password = z.string().min(10).max(200);

export const StaffInvite = z
  .object({
    id: Uuid,
    salonId: Uuid,
    staffId: Uuid,
    email: Email,
    status: z.enum(['pending', 'accepted', 'revoked', 'expired']),
    expiresAt: IsoDateTime,
    acceptedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'StaffInvite' });
export type StaffInvite = z.infer<typeof StaffInvite>;

/** Create an invite for an existing staff member. Defaults to the staff member's own email. */
export const CreateInvite = z
  .object({
    email: Email.optional(),
  })
  .meta({ id: 'CreateInvite' });
export type CreateInvite = z.infer<typeof CreateInvite>;

/** Accept an invite: set a password and (optionally) a display name, then land in a session. */
export const InviteAccept = z
  .object({
    token: z.string().min(1),
    password: Password,
    displayName: NonEmptyName.optional(),
  })
  .meta({ id: 'InviteAccept' });
export type InviteAccept = z.infer<typeof InviteAccept>;

/** Request a password reset. Always answered 200 {ok:true} (no user enumeration). */
export const PasswordResetRequest = z
  .object({
    email: Email,
  })
  .meta({ id: 'PasswordResetRequest' });
export type PasswordResetRequest = z.infer<typeof PasswordResetRequest>;

/** Confirm a password reset with the emailed token; does NOT auto-login. */
export const PasswordResetConfirm = z
  .object({
    token: z.string().min(1),
    password: Password,
  })
  .meta({ id: 'PasswordResetConfirm' });
export type PasswordResetConfirm = z.infer<typeof PasswordResetConfirm>;

/** Patch a location: every CreateLocation field is optional; only provided fields are updated. */
export const UpdateLocation = CreateLocation.partial().meta({ id: 'UpdateLocation' });
export type UpdateLocation = z.infer<typeof UpdateLocation>;
